import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";

import {
  filterTriggers,
  isInSameOrder,
  paginate,
  preserveOrder,
  sortTriggers,
  type PaginationState,
  type TriggerViewParams,
} from "../triggerList";
import type { TriggerV2Record } from "../TriggerV2Record";

import { triggersV2QueryKeys } from "./triggersV2QueryKeys";

/** One page of the triggers list, while it loads, and why it failed. */
export interface TriggersResult {
  /** Undefined until the first load succeeds. */
  paginationState: PaginationState | undefined;
  isPending: boolean;
  /**
   * The first load's failure, with no `paginationState`; or a background
   * refresh's, with the rows from the last fetch that succeeded.
   */
  error: Error | null;
}

/**
 * One page of the org's triggers, in the order {@link useSortedTriggerRows}
 * keeps.
 */
export function useTriggers(
  params: TriggerViewParams,
  orgId?: string,
): TriggersResult {
  const query = useTriggersList(useCurrentOrgId(orgId));
  const rows = useSortedTriggerRows(query.data, params);
  const paginationState = React.useMemo(
    () => rows && paginate(rows, params.page, params.pageSize),
    [rows, params.page, params.pageSize],
  );
  return { paginationState, isPending: query.isPending, error: query.error };
}

/**
 * Every trigger in the org, unsorted and unfiltered, for a reader that needs
 * the whole list rather than a page of the triggers table. Shares the table's
 * cache, so a toggle or delete shows in both.
 */
export function useAllTriggers(orgId?: string) {
  return useTriggersList(useCurrentOrgId(orgId));
}

/**
 * Every trigger in the org, every page of them. The API can't sort or filter
 * triggers yet, so the list is fetched whole and sorted, filtered and paged
 * in the browser.
 */
function useTriggersList(orgId: string) {
  const { triggerV2Service } = useDomainServices();

  return useQuery({
    queryKey: triggersV2QueryKeys.list(orgId),
    queryFn: ({ signal }) =>
      triggerV2Service.listAllTriggers(orgId, { abortSignal: signal }),
  });
}

/**
 * The triggers matching `params.filter`, sorted when the list first shows and
 * whenever the sort or filters change. Between those they keep the order
 * they were last sorted in: a fetch or a toggle updates rows in place, a
 * trigger that no longer matches or exists drops out, and a new one goes
 * where the sort puts it. Changing page doesn't re-sort.
 */
function useSortedTriggerRows(
  triggers: readonly TriggerV2Record[] | undefined,
  params: TriggerViewParams,
): readonly TriggerV2Record[] | undefined {
  // The rows as last sorted, and the params they were sorted for
  const [sorted, setSorted] = React.useState<{
    params: TriggerViewParams;
    rows: readonly TriggerV2Record[];
  } | null>(null);

  if (triggers === undefined) {
    return undefined;
  }

  const filtered = filterTriggers(triggers, params.filter);
  const sameOrder = sorted !== null && isInSameOrder(sorted.params, params);

  const rows = sameOrder
    ? preserveOrder(sorted.rows, filtered, params.sort)
    : sortTriggers(filtered, params.sort);

  const changed = !sameOrder || !isSameRows(rows, sorted.rows);

  if (changed) {
    // Store the new order; React re-renders this hook once before going on.
    setSorted({ params, rows });
  }

  return changed ? rows : sorted.rows;
}

function isSameRows(
  a: readonly TriggerV2Record[],
  b: readonly TriggerV2Record[],
): boolean {
  return a.length === b.length && a.every((row, index) => row === b[index]);
}
